import { GridLogicOperator } from '@mui/x-data-grid-premium';
import { createFileRoute } from '@tanstack/react-router';
import { nanoid } from 'nanoid';
import { z } from 'zod';

import { WizardPlanTable } from '#screens/wizard-plan-table/wizard-plan-table.js';

import type { QueryBuilder } from '@cadstrom/cadstrom-shared';
import type { GridFilterItem, GridFilterModel } from '@mui/x-data-grid-premium';

/**
 * Sentinel passed as `associatedUserName` or `assignedToOrgName` to indicate
 * the filter should match plans with no assigned user / org (i.e. `isEmpty`).
 */
export const UNASSIGNED_FILTER_SENTINEL = '__unassigned__';

const searchSchema = z.object({
	/** Filter by status (equals). Defaults to 'pending' when omitted. Special value of 'any' matches all statuses. */
	status: z.string().optional(),
	/** Upper bound for createdAt (ISO timestamp string). */
	createdBefore: z.string().optional(),
	/** Lower bound for createdAt (ISO timestamp string). */
	createdAfter: z.string().optional(),
	/** Upper bound for deadlineAt (ISO timestamp string). */
	deadlineBefore: z.string().optional(),
	/** Lower bound for deadlineAt (ISO timestamp string). */
	deadlineAfter: z.string().optional(),
	/** Upper bound for completedAt (ISO timestamp string). */
	completedBefore: z.string().optional(),
	/** Lower bound for completedAt (ISO timestamp string). */
	completedAfter: z.string().optional(),
	/** Filter associatedUserName by equals. */
	associatedUserName: z.string().optional(),
	/** Filter assignedToOrgName by equals. */
	assignedToOrgName: z.string().optional(),
	/** Filter manufacturerPartNumber by equals. */
	mpn: z.string().optional(),
	/** Filter packageId by equals. */
	packageId: z.string().optional(),
	/** When true, show only op-eval plans; when false, omit op-eval plans. */
	isTrainingPlan: z.preprocess(
		(v) =>
			v === 'true' ? true
			: v === 'false' ? false
			: v,
		z.boolean().optional(),
	),
});

type PlansPageSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_authorization-gate/wizard/admin/plans/')({
	validateSearch: searchSchema,
	head: () => ({ meta: [{ title: 'Plans • Wizard Admin • Cadstrom' }] }),
	component: WizardAdminPlansPage,
});

function WizardAdminPlansPage() {
	const search = Route.useSearch();

	return (
		<div className='flex min-h-0 grow basis-0 flex-col p-3'>
			<WizardPlanTable initialFilterModel={buildInitialFilterModel(search)} />
		</div>
	);
}

/**
 * Converts an ISO timestamp string to a millisecond timestamp string for use
 * as a QB filter value. Returns null when the value is absent or unparseable.
 *
 * @param iso - ISO timestamp string, or undefined when the param is absent.
 * @returns Millisecond timestamp as a string, or null.
 */
function isoToMs(iso: string | undefined): string | null {
	if (iso === undefined) {
		return null;
	}
	const ms = new Date(iso).getTime();
	return Number.isFinite(ms) ? String(ms) : null;
}

/**
 * Builds a GridFilterModel from URL search params.
 * When `status` is absent the default 'pending' filter is applied.
 *
 * @param search - Validated search params from the route.
 * @returns A GridFilterModel suitable for passing to WizardPlanTable as initialFilterModel.
 */
function buildInitialFilterModel(search: PlansPageSearch): GridFilterModel {
	const items: GridFilterItem[] = [];

	// Status (defaults to 'pending')
	if (search.status !== 'any') {
		items.push({ id: nanoid(), field: 'status', operator: 'is', value: search.status ?? 'pending' });
	}

	// Timestamp bounds
	const addTimestamp = (field: string, operator: QueryBuilder.ColumnFilterOperator, iso: string | undefined) => {
		const ms = isoToMs(iso);
		if (ms !== null) {
			items.push({ id: nanoid(), field, operator, value: ms });
		}
	};
	addTimestamp('createdAt', 'lessThan', search.createdBefore);
	addTimestamp('createdAt', 'greaterThan', search.createdAfter);
	addTimestamp('deadlineAt', 'lessThan', search.deadlineBefore);
	addTimestamp('deadlineAt', 'greaterThan', search.deadlineAfter);
	addTimestamp('completedAt', 'lessThan', search.completedBefore);
	addTimestamp('completedAt', 'greaterThan', search.completedAfter);

	// String equals filters (sentinel value means "is empty / unassigned")
	if (search.associatedUserName !== undefined) {
		if (search.associatedUserName === UNASSIGNED_FILTER_SENTINEL) {
			items.push({ id: nanoid(), field: 'associatedUserNames', operator: 'isEmpty' });
		} else {
			items.push({
				id: nanoid(),
				field: 'associatedUserNames',
				operator: 'contains',
				value: search.associatedUserName,
			});
		}
	}
	if (search.assignedToOrgName !== undefined) {
		if (search.assignedToOrgName === UNASSIGNED_FILTER_SENTINEL) {
			items.push({ id: nanoid(), field: 'assignedToOrgName', operator: 'isEmpty' });
		} else {
			items.push({ id: nanoid(), field: 'assignedToOrgName', operator: 'equals', value: search.assignedToOrgName });
		}
	}
	if (search.mpn !== undefined) {
		items.push({ id: nanoid(), field: 'manufacturerPartNumber', operator: 'equals', value: search.mpn });
	}
	if (search.packageId !== undefined) {
		items.push({ id: nanoid(), field: 'packageId', operator: 'equals', value: search.packageId });
	}
	if (search.isTrainingPlan !== undefined) {
		items.push({ id: nanoid(), field: 'isTrainingPlan', operator: 'is', value: search.isTrainingPlan });
	}

	return { items, logicOperator: GridLogicOperator.And };
}
